import React from 'react';
import TopBar from './TopBar';
import { ELEMENTS, EMPTY_ELEMENT_ENTRY } from '../utils/constants';
import { getCycleSummary } from '../utils/cycle';

function IndividualLogFlow({ elementKey, todayDraft, updateDraft, state, setRoute, saveElement, navigateBack }) {
  const element = ELEMENTS.find(el => el.key === elementKey);

  if (!element) {
    return (
      <div className="p-4">
        <div className="text-lg font-semibold mb-4 dark:text-dark-text">Error: Element not found</div>
        <div className="dark:text-dark-textSecondary">elementKey: {elementKey}</div>
        <div className="dark:text-dark-textSecondary">Available elements: {ELEMENTS.map(el => el.key).join(', ')}</div>
        <button onClick={() => setRoute("home")} className="mt-4 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600">
          Back to Home
        </button>
      </div>
    );
  }

  const draft = { ...EMPTY_ELEMENT_ENTRY, ...(todayDraft?.[elementKey] || {}) };
  const cycleSummary = getCycleSummary(state.cycleTracking);
  const showCycleInfo = cycleSummary.enabled && cycleSummary.cycleDay;

  // Check if there are unsaved changes
  const today = new Date().toISOString().slice(0, 10);
  const savedEntry = state?.entries?.find(entry => entry.date === today);
  const savedData = savedEntry?.values?.[elementKey] || { text: "", action: "", redirect: false, cycleNote: "", rating: null };
  const hasUnsavedChanges = draft.text !== savedData.text ||
    draft.action !== savedData.action ||
    draft.redirect !== savedData.redirect ||
    (draft.cycleNote || "") !== (savedData.cycleNote || "") ||
    draft.rating !== savedData.rating;

  const handleSave = () => {
    if (saveElement) {
      saveElement(elementKey, draft);
    }
  };

  const handleNext = () => {
    // Find next element
    const currentIndex = ELEMENTS.findIndex(el => el.key === elementKey);
    const nextIndex = (currentIndex + 1) % ELEMENTS.length;
    const nextElement = ELEMENTS[nextIndex];
    if (setRoute) {
      setRoute(`log-${nextElement.key}`);
    }
  };

  const handleReturnHome = () => {
    if (setRoute) {
      setRoute("home");
    }
  };

  const handleBack = () => {
    // Use browser history to go back to the previous page
    if (navigateBack) {
      navigateBack();
    } else {
      // Fallback to home if navigateBack is not available
      if (setRoute) {
        setRoute("home");
      }
    }
  };

  const handleTabSwitch = (newElementKey) => {
    // Just switch tabs without saving
    if (setRoute) {
      setRoute(`log-${newElementKey}`);
    }
  };

  const isFireElement = elementKey === 'fire';

  return (
    <div className="p-4 h-full flex flex-col">
      <TopBar title={`Log • ${element.label}`} onBack={handleBack} />

      {/* Element Navigation - Top */}
      <div className="mt-3 mb-4">
        <div className="flex gap-1 overflow-x-auto pb-2">
          {ELEMENTS.map((el) => (
            <button
              key={el.key}
              onClick={() => handleTabSwitch(el.key)}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${el.key === elementKey
                ? 'bg-blue-600 dark:bg-blue-500 text-white'
                : 'bg-gray-100 dark:bg-dark-surfaceHover text-gray-700 dark:text-dark-textSecondary hover:bg-gray-200 dark:hover:bg-dark-border'
                }`}
            >
              <span className="mr-1">{el.emoji}</span>
              {el.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1">
        {showCycleInfo && (
          <div className="mb-4 p-3 rounded-lg bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-700">
            <div className="text-xs uppercase tracking-wide text-pink-700 dark:text-pink-200">Cycle</div>
            <div className="text-sm font-medium text-pink-800 dark:text-pink-100 mt-1">
              Day {cycleSummary.cycleDay}{cycleSummary.totalLength ? ` of ${cycleSummary.totalLength}` : ''}
            </div>
            {cycleSummary.stage && (
              <div className="text-xs text-pink-700 dark:text-pink-200 mt-1">
                Stage: {cycleSummary.stage.name} (day {cycleSummary.dayOfStage} of {cycleSummary.stage.length})
              </div>
            )}
          </div>
        )}

        <div className="text-sm text-gray-600 dark:text-dark-textSecondary">Utopia reminder</div>
        <div className="text-xs text-gray-800 dark:text-dark-text mb-3">{state.utopia[elementKey] || 'Your utopia description here.'}</div>

        <div className="mb-4 flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px] p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <strong>{element.label}:</strong> {element.hint}
            </div>
          </div>
          {state.ratingTracking?.enabled && (
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium dark:text-dark-text block mb-2">How do you feel about {element.label} today?</label>
              <div className="flex gap-2 flex-wrap">
                {state.ratingTracking.emojiSet.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => {
                      const newRating = draft.rating === emoji ? null : emoji;
                      updateDraft?.(elementKey, "rating", newRating);
                    }}
                    className={`text-2xl px-3 py-2 rounded-lg border-2 transition-colors ${
                      draft.rating === emoji
                        ? 'border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg hover:border-gray-400 dark:hover:border-dark-border'
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mb-3">
          <label className="text-sm font-medium dark:text-dark-text">Current state</label>
          <textarea
            autoFocus
            maxLength={300}
            rows={4}
            value={draft.text}
            onChange={(e) => updateDraft?.(elementKey, "text", e.target.value)}
            className="w-full mt-2 rounded-md border dark:border-dark-border p-3 text-sm bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text"
            placeholder={`How is your ${element.label.toLowerCase()} today?`}
          />
          <div className="text-xs text-gray-400 dark:text-dark-muted mt-1">{(draft.text || "").length}/300</div>
        </div>

        <div className="mb-3">
          <label className="text-sm font-medium dark:text-dark-text">Action for this element</label>
          <textarea
            maxLength={300}
            rows={3}
            value={draft.action}
            onChange={(e) => updateDraft?.(elementKey, "action", e.target.value)}
            className="w-full mt-2 rounded-md border dark:border-dark-border p-3 text-sm bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text"
            placeholder={`What action will help your ${element.label.toLowerCase()} move toward utopia?`}
          />
          <div className="text-xs text-gray-400 dark:text-dark-muted mt-1">{(draft.action || "").length}/300</div>
        </div>

        <div className="mb-3">
          <label className="text-sm font-medium dark:text-dark-text">Cycle notes (optional)</label>
          <textarea
            maxLength={300}
            rows={3}
            value={draft.cycleNote}
            onChange={(e) => updateDraft?.(elementKey, "cycleNote", e.target.value)}
            className="w-full mt-2 rounded-md border dark:border-dark-border p-3 text-sm bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text"
            placeholder="Anything to notice about your cycle today?"
          />
          <div className="text-xs text-gray-400 dark:text-dark-muted mt-1">{(draft.cycleNote || "").length}/300</div>
        </div>

        <div className="mb-6 flex items-center gap-3">
          <input
            id={`redirect-${elementKey}`}
            type="checkbox"
            checked={draft.redirect}
            onChange={(e) => updateDraft?.(elementKey, "redirect", e.target.checked)}
            className="rounded"
          />
          <label htmlFor={`redirect-${elementKey}`} className="text-sm text-gray-700 dark:text-dark-textSecondary">
            Is redirectional action required?
          </label>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <button
          onClick={handleSave}
          className={`w-full py-3 rounded-md font-medium ${hasUnsavedChanges
            ? 'bg-green-600 dark:bg-green-500 text-white hover:bg-green-700 dark:hover:bg-green-600'
            : 'bg-gray-300 dark:bg-dark-border text-gray-500 dark:text-dark-muted cursor-not-allowed'
            }`}
          disabled={!hasUnsavedChanges}
        >
          {hasUnsavedChanges ? 'Save Changes' : 'No Changes to Save'}
        </button>

        <div className="flex gap-2">
          <button
            onClick={handleReturnHome}
            className="flex-1 py-2 rounded-md border dark:border-dark-border dark:bg-dark-surface dark:text-dark-text hover:bg-gray-50 dark:hover:bg-dark-surfaceHover"
          >
            Return Home
          </button>
          {!isFireElement && (
            <button
              onClick={handleNext}
              className="flex-1 py-2 rounded-md bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600"
            >
              Next Element
            </button>
          )}
        </div>
      </div>

      {/* Element Navigation - Bottom */}
      <div className="mt-4">
        <div className="text-xs text-gray-500 dark:text-dark-textSecondary mb-2">Quick Navigation</div>
        <div className="flex gap-1 overflow-x-auto pb-2">
          {ELEMENTS.map((el) => (
            <button
              key={el.key}
              onClick={() => handleTabSwitch(el.key)}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${el.key === elementKey
                ? 'bg-blue-600 dark:bg-blue-500 text-white'
                : 'bg-gray-100 dark:bg-dark-surfaceHover text-gray-700 dark:text-dark-textSecondary hover:bg-gray-200 dark:hover:bg-dark-border'
                }`}
            >
              <span className="mr-1">{el.emoji}</span>
              {el.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default IndividualLogFlow;
